import { parseIsoInstant } from '@/domain/health/time'

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u
const TIME_PATTERN = /^(\d{2}):(\d{2})$/u
const INVALID_LOCAL_INPUT = '日期或时间无效'
const NONEXISTENT_LOCAL_TIME = '所选本地时间不存在'

interface WallDateTime {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

function strictUtcTimestamp(parts: WallDateTime): number {
  const result = new Date(0)
  result.setUTCFullYear(parts.year, parts.month - 1, parts.day)
  result.setUTCHours(parts.hour, parts.minute, 0, 0)
  if (
    result.getUTCFullYear() !== parts.year ||
    result.getUTCMonth() + 1 !== parts.month ||
    result.getUTCDate() !== parts.day ||
    result.getUTCHours() !== parts.hour ||
    result.getUTCMinutes() !== parts.minute
  ) {
    throw new Error(INVALID_LOCAL_INPUT)
  }
  return result.getTime()
}

function parseWallDateTime(localDate: string, localTime: string): WallDateTime {
  const dateMatch = DATE_PATTERN.exec(localDate)
  const timeMatch = TIME_PATTERN.exec(localTime)
  if (dateMatch === null || timeMatch === null) throw new Error(INVALID_LOCAL_INPUT)
  const parts = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
  }
  if (parts.hour > 23 || parts.minute > 59) throw new Error(INVALID_LOCAL_INPUT)
  strictUtcTimestamp(parts)
  return parts
}

function wallFormatter(timeZone: string): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
  } catch {
    throw new Error('时区必须是有效的 IANA 时区')
  }
}

function partsFromFormatter(formatter: Intl.DateTimeFormat, instant: Date): WallDateTime {
  const values = Object.fromEntries(
    formatter
      .formatToParts(instant)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  )
  const required = (name: string): number => {
    const value = values[name]
    if (value === undefined) throw new Error(INVALID_LOCAL_INPUT)
    return Number(value)
  }
  return {
    year: required('year'),
    month: required('month'),
    day: required('day'),
    hour: required('hour'),
    minute: required('minute'),
  }
}

function sameWallDateTime(left: WallDateTime, right: WallDateTime): boolean {
  return (
    left.year === right.year &&
    left.month === right.month &&
    left.day === right.day &&
    left.hour === right.hour &&
    left.minute === right.minute
  )
}

/** Resolve an IANA wall time; when a fall-back hour repeats, choose its earlier instant. */
export function zonedLocalDateTimeToIso(
  localDate: string,
  localTime: string,
  timeZone: string,
): string {
  const target = parseWallDateTime(localDate, localTime)
  const targetWallTimestamp = strictUtcTimestamp(target)
  const formatter = wallFormatter(timeZone)
  const offsets = new Set<number>()

  for (let hour = -36; hour <= 36; hour += 1) {
    const sampleTimestamp = targetWallTimestamp + hour * 60 * 60 * 1000
    const rendered = partsFromFormatter(formatter, new Date(sampleTimestamp))
    offsets.add(strictUtcTimestamp(rendered) - sampleTimestamp)
  }

  const candidates = [...offsets]
    .map((offset) => targetWallTimestamp - offset)
    .filter((candidate) =>
      sameWallDateTime(partsFromFormatter(formatter, new Date(candidate)), target),
    )
    .sort((left, right) => left - right)

  if (candidates.length === 0) throw new Error(NONEXISTENT_LOCAL_TIME)
  return new Date(candidates[0] as number).toISOString()
}

export function localFieldsAt(
  instant: Date | string,
  timeZone: string,
): { localDate: string; localTime: string } {
  const date = typeof instant === 'string' ? parseIsoInstant(instant) : instant
  const parts = partsFromFormatter(wallFormatter(timeZone), date)
  return {
    localDate: `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`,
    localTime: `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`,
  }
}
